import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Key, ShieldCheck, Warning, Crown, CaretRight, IdentificationCard } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalCard, BrutalInput } from "../components/Brutal";
import { useAuth } from "../AuthContext";

function ProfileCard() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);
  const dirty = name.trim() !== (user?.name || "") || (phone || "").trim() !== (user?.phone || "");

  const save = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) {
      toast.error(t("settings.name_too_short"));
      return;
    }
    setBusy(true);
    try {
      await api.patch("/auth/profile", { name: name.trim(), phone: phone.trim() || null });
      toast.success(t("settings.profile_saved"));
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <BrutalCard>
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0">
          <IdentificationCard size={18} weight="bold" />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">{t("settings.profile_title")}</h2>
          <p className="text-xs text-zinc-500 mt-0.5">{t("settings.profile_subtitle")}</p>
        </div>
      </div>
      <form onSubmit={save} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold mb-1.5">{t("settings.display_name")}</label>
          <BrutalInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
            maxLength={100}
            data-testid="profile-name-input"
          />
          <p className="text-[10px] text-zinc-400 mt-1">{t("settings.display_name_hint")}</p>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1.5">{t("settings.phone")}</label>
          <BrutalInput
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={50}
            placeholder="(555) 555-0100"
            data-testid="profile-phone-input"
          />
        </div>
        <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg p-3" data-testid="profile-email-note">
          {t("settings.email_locked", { email: user?.email })}
        </div>
        <BrutalButton type="submit" className="w-full" disabled={busy || !dirty} data-testid="profile-save-btn">
          {busy ? t("settings.updating") : t("settings.save_profile")}
        </BrutalButton>
      </form>
    </BrutalCard>
  );
}

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user, mustChangePassword, isOwner, refresh } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error(t("settings.too_short"));
      return;
    }
    if (next !== confirm) {
      toast.error(t("settings.mismatch"));
      return;
    }
    if (next === current) {
      toast.error(t("settings.same_as_current"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success(t("settings.password_changed"));
      setCurrent("");
      setNext("");
      setConfirm("");
      // Pick up the cleared must_change_password flag from /auth/me, then go home.
      await refresh();
      setTimeout(() => nav("/", { replace: true }), 400);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("settings.title")}</h1>
        <p className="text-sm sm:text-base text-zinc-600 mt-1">{t("settings.subtitle")}</p>
      </div>

      {mustChangePassword && (
        <div
          className="border border-amber-300 bg-amber-50 rounded-xl p-4 flex items-start gap-3"
          data-testid="force-rotate-banner"
        >
          <Warning size={20} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-900 text-sm">{t("settings.force_title")}</h3>
            <p className="text-sm text-amber-800 mt-0.5">{t("settings.force_body")}</p>
          </div>
        </div>
      )}

      <ProfileCard />

      <BrutalCard>
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0">
            <Key size={18} weight="bold" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">{t("settings.change_password")}</h2>
            <p className="text-xs text-zinc-500 mt-0.5 truncate">
              {user?.email}
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold mb-1.5">{t("settings.current_password")}</label>
            <BrutalInput
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="current-password-input"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5">{t("settings.new_password")}</label>
            <BrutalInput
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              placeholder={t("settings.min_chars")}
              data-testid="new-password-input"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5">{t("settings.confirm_password")}</label>
            <BrutalInput
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              data-testid="confirm-password-input"
            />
          </div>

          <div className="flex items-start gap-2 text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg p-3">
            <ShieldCheck size={14} weight="bold" className="shrink-0 mt-0.5" />
            <span>{t("settings.tip")}</span>
          </div>

          <div className="flex gap-3 pt-1">
            {!mustChangePassword && (
              <BrutalButton type="button" variant="secondary" className="flex-1" onClick={() => nav(-1)}>
                {t("common.cancel")}
              </BrutalButton>
            )}
            <BrutalButton
              type="submit"
              className="flex-1"
              disabled={busy}
              data-testid="change-password-submit"
            >
              {busy ? t("settings.updating") : t("settings.update_password")}
            </BrutalButton>
          </div>
        </form>
      </BrutalCard>

      {isOwner && (
        <Link
          to="/settings/super-owners"
          className="block group"
          data-testid="super-owners-link"
        >
          <BrutalCard className="hover:border-[#c9a24a] transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#fbf6e7] border-2 border-[#c9a24a] flex items-center justify-center shrink-0">
                <Crown size={18} weight="fill" className="text-[#8a6a1c]" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold">{t("super_owners.link_title")}</h2>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {t("super_owners.link_subtitle")}
                </p>
              </div>
              <CaretRight size={18} weight="bold" className="text-zinc-400 group-hover:text-zinc-900 transition-colors shrink-0" />
            </div>
          </BrutalCard>
        </Link>
      )}
    </div>
  );
}
